import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/sheet';

import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import type { ToastGuid } from '@Types/toastGuid';
import { useVenues } from '@Hooks/useVenues';
import { useCreateToastGuid } from '@Hooks/useCreateToastGuid';
import { useUpdateToastGuid } from '@Hooks/useUpdateToastGuid';
import { Combobox } from '../Combobox';

const FormSchema = z.object({
  name: z.string().min(1, {
    message: 'Name is required.',
  }),
  guid: z.string().min(1, {
    message: 'GUID is required.',
  }),
  venueId: z.string().min(1, {
    message: 'Venue is required.',
  }),
});

type FormValues = z.infer<typeof FormSchema>;

export interface ToastGuidSheetProps {
  toastGuid?: ToastGuid | null;
  isOpen: boolean;
  setIsOpen: (newIsOpen: boolean) => void;
  onSuccess?: () => void;
}

export default function ToastGuidSheet({
  toastGuid,
  isOpen,
  setIsOpen,
  onSuccess,
}: ToastGuidSheetProps) {
  const isEditMode = !!toastGuid;

  const createMutation = useCreateToastGuid();
  const updateMutation = useUpdateToastGuid();

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: toastGuid?.name ?? '',
      guid: toastGuid?.guid ?? '',
      venueId: toastGuid?.venueId ?? '',
    },
  });

  const venues = useVenues({
    enabled: isOpen,
    pageSize: -1,
  });

  const venueItems = (venues.data ?? [])
    .map((venue) => ({
      value: venue.id,
      label: venue.name,
    }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    );

  useEffect(() => {
    if (isOpen) {
      form.reset({
        name: toastGuid?.name ?? '',
        guid: toastGuid?.guid ?? '',
        venueId: toastGuid?.venueId ?? '',
      });
    }
  }, [toastGuid, isOpen, form]);

  const onSubmit = async (values: FormValues) => {
    form.clearErrors('root');

    try {
      if (isEditMode && toastGuid) {
        await updateMutation.mutateAsync({
          id: toastGuid.id,
          payload: {
            name: values.name,
            guid: values.guid,
            venueId: values.venueId,
          },
        });
      } else {
        await createMutation.mutateAsync({
          name: values.name,
          guid: values.guid,
          venueId: values.venueId,
        });
      }

      onSuccess?.();
      setIsOpen(false);
    } catch (error) {
      console.error('Error saving toast guid:', error);
      form.setError('root', {
        type: 'server',
        message:
          error instanceof Error
            ? error.message
            : 'We were unable to save this Toast GUID. Please check your changes and try again.',
      });
    }
  };

  const handleOnOpenChange = (newIsSheetOpen: boolean) => {
    setIsOpen(newIsSheetOpen);

    if (!newIsSheetOpen) {
      form.reset({
        name: toastGuid?.name ?? '',
        guid: toastGuid?.guid ?? '',
        venueId: toastGuid?.venueId ?? '',
      });
    }
  };

  const rootError = form.formState.errors.root?.message;
  const isSubmitting =
    form.formState.isSubmitting ||
    createMutation.isPending ||
    updateMutation.isPending;

  return (
    <Sheet open={isOpen} onOpenChange={handleOnOpenChange}>
      <SheetContent
        onPointerDownOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="h-full">
            <div className="flex h-full flex-col">
              <SheetHeader>
                <SheetTitle>
                  {isEditMode ? `Edit Toast GUID` : 'Create Toast GUID'}
                </SheetTitle>
              </SheetHeader>

              <SheetBody className="grow overflow-auto">
                {rootError && (
                  <div className="mb-4">
                    <Alert variant="destructive">
                      <AlertTitle>Something went wrong</AlertTitle>
                      <AlertDescription>{rootError}</AlertDescription>
                    </Alert>
                  </div>
                )}

                <div className="space-y-6 py-3">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. Special Discount"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>The Toast GUID name.</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="guid"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>GUID</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. 12345678-1234-1234-1234-123456789012"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          The Toast GUID identifier.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="venueId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Venue</FormLabel>
                        <Combobox
                          inForm
                          id="venueId"
                          items={venueItems}
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Select a venue"
                          disabled={venues.isLoading || !venues.data?.length}
                          loading={venues.isLoading}
                          inModal
                        />
                        <FormDescription>
                          The venue this GUID is associated with.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </SheetBody>

              <SheetFooter>
                <Button type="submit" variant="primary" loading={isSubmitting}>
                  {isEditMode ? 'Update' : 'Create'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => handleOnOpenChange(false)}
                >
                  Cancel
                </Button>
              </SheetFooter>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
